import { useState } from 'react';
import { 
  Save, 
  Store, 
  Globe, 
  Mail, 
  Phone, 
  MapPin,
  Facebook,
  Instagram,
  Twitter,
  Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

export default function AdminSettings() {
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    // Store Info
    store_name: 'MRC Boutique, LLC',
    store_description: 'Boutique an liy pou tout sa ou bezwen',
    logo_url: '',
    favicon_url: '',
    
    // Contact
    email: 'mrc@mrcboutique.online',
    phone: '+1 (772) 475-5207',
    whatsapp: '+1 (772) 475-5207',
    address: 'Miami, FL, USA',
    
    // Social
    facebook: 'https://www.facebook.com/share/1Csi1am7Vj/?mibextid=wwXIfr',
    instagram: '',
    twitter: '',
    tiktok: 'https://www.tiktok.com/@un.simple.clic',
    
    // Regional
    currency: 'USD',
    language: 'ht',
    timezone: 'America/New_York',
    tax_rate: '0',
    
    // Features
    enable_reviews: true,
    enable_wishlist: true,
    enable_compare: false,
    maintenance_mode: false
  });

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    // Simulate save
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success('Paramèt yo sove!');
    setSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="admin-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#001A3E]">Paramèt</h1>
          <p className="text-gray-500">Konfigire boutik ou</p>
        </div>
        <Button 
          className="bg-[#001A3E] hover:bg-[#001A3E]/90"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Ap Sove...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Sove
            </>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Store Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="h-5 w-5" />
              Enfòmasyon Boutik
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="store_name">Non Boutik</Label>
              <Input
                id="store_name"
                value={settings.store_name}
                onChange={(e) => handleChange('store_name', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="store_description">Deskripsyon</Label>
              <Textarea
                id="store_description"
                value={settings.store_description}
                onChange={(e) => handleChange('store_description', e.target.value)}
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="logo_url">URL Logo</Label>
              <Input
                id="logo_url"
                type="url"
                value={settings.logo_url}
                onChange={(e) => handleChange('logo_url', e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div>
              <Label htmlFor="favicon_url">URL Favicon</Label>
              <Input
                id="favicon_url"
                type="url"
                value={settings.favicon_url}
                onChange={(e) => handleChange('favicon_url', e.target.value)}
                placeholder="https://..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Kontak
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={settings.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="phone">Telefòn</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="phone"
                  value={settings.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="whatsapp">WhatsApp</Label>
              <Input
                id="whatsapp"
                value={settings.whatsapp}
                onChange={(e) => handleChange('whatsapp', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="address">Adrès</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Textarea
                  id="address"
                  value={settings.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  className="pl-10"
                  rows={2}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Social Links */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Rezo Sosyal
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="facebook">Facebook</Label>
              <div className="relative">
                <Facebook className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="facebook"
                  value={settings.facebook}
                  onChange={(e) => handleChange('facebook', e.target.value)}
                  className="pl-10"
                  placeholder="https://facebook.com/..."
                />
              </div>
            </div>
            <div>
              <Label htmlFor="instagram">Instagram</Label>
              <div className="relative">
                <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="instagram"
                  value={settings.instagram}
                  onChange={(e) => handleChange('instagram', e.target.value)}
                  className="pl-10"
                  placeholder="https://instagram.com/..."
                />
              </div>
            </div>
            <div>
              <Label htmlFor="twitter">Twitter</Label>
              <div className="relative">
                <Twitter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="twitter"
                  value={settings.twitter}
                  onChange={(e) => handleChange('twitter', e.target.value)}
                  className="pl-10"
                  placeholder="https://twitter.com/..."
                />
              </div>
            </div>
            <div>
              <Label htmlFor="tiktok">TikTok</Label>
              <Input
                id="tiktok"
                value={settings.tiktok}
                onChange={(e) => handleChange('tiktok', e.target.value)}
                placeholder="https://tiktok.com/..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Regional Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Rejyon</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="currency">Deviz</Label>
              <Select value={settings.currency} onValueChange={(v) => handleChange('currency', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="HTG">HTG (Goud)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="language">Lang Prensipal</Label>
              <Select value={settings.language} onValueChange={(v) => handleChange('language', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ht">Kreyòl Ayisyen</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="es">Español</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="timezone">Zòn Tan</Label>
              <Select value={settings.timezone} onValueChange={(v) => handleChange('timezone', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                  <SelectItem value="America/Port-au-Prince">Haiti (HT)</SelectItem>
                  <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="tax_rate">To Taks (%)</Label>
              <Input
                id="tax_rate"
                type="number"
                step="0.01"
                value={settings.tax_rate}
                onChange={(e) => handleChange('tax_rate', e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Features */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Fonksyon</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Revi</p>
                  <p className="text-sm text-gray-500">Pèmèt kliyan kite revi</p>
                </div>
                <Switch
                  checked={settings.enable_reviews}
                  onCheckedChange={(v) => handleChange('enable_reviews', v)}
                />
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Wishlist</p>
                  <p className="text-sm text-gray-500">Lis pwodwi prefere</p>
                </div>
                <Switch
                  checked={settings.enable_wishlist}
                  onCheckedChange={(v) => handleChange('enable_wishlist', v)}
                />
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Konpare</p>
                  <p className="text-sm text-gray-500">Konpare pwodwi</p>
                </div>
                <Switch
                  checked={settings.enable_compare}
                  onCheckedChange={(v) => handleChange('enable_compare', v)}
                />
              </div>
              <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                <div>
                  <p className="font-medium text-red-700">Mòd Manteni</p>
                  <p className="text-sm text-red-500">Fèmen sit la tanporèman</p>
                </div>
                <Switch
                  checked={settings.maintenance_mode}
                  onCheckedChange={(v) => handleChange('maintenance_mode', v)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
